import { LoadingButton } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PaymentMethodInfo from "components/settings/shared/billing-details/paymentMethodInfo";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { User, useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { HubPlan } from "data/models";
import { ReactQueryMutationError, extractClip } from "data/mutations";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntercom } from "react-use-intercom";
import { toTitleCase } from "utils/commons";
import { resolveIdentityId } from "utils/identity";
import { ClipConfiguration, QuoteSummary } from "../../models";

interface PurchaseWithDollarsProps {
  clipConfig: ClipConfiguration;
  quoteSummary: QuoteSummary;
  plan: HubPlan;
  resetClipConfig: () => void;
  setSubStep: React.Dispatch<React.SetStateAction<number>>;
}

const formatDollars = (n: number) => {
  return n.toLocaleString("en-AU", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
};

export const PurchaseWithDollars = (props: PurchaseWithDollarsProps) => {
  const [identityState] = useIdentity();
  const { show } = useIntercom();
  const identityId = resolveIdentityId(identityState);
  const [paymentMethod, setPaymentMethod] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const calculateTaxExcluded = (amount: number) => {
    return amount - amount / 11;
  };

  const { enqueueMutationFailed } = useCustomSnackbars();

  const submitExtractJob = useMutation({
      mutationFn: (clipId: string) => extractClip(clipId, "invoice"),
      onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [QueryKeys.clipsKey, identityId],
          }),
            enqueueSnackbar("Clip has been submitted for extraction.", {
              variant: "success",
            });
          props.resetClipConfig();
        },
      onError: (error: Error) => {
          enqueueMutationFailed(error.toString());
        },
    });
  return (
    <>
      <Box
        display="flex"
        flexDirection="column"
        gap={2}
        maxHeight="calc(86vh - 64px)"
        overflow="auto"
      >
        <TextField
          sx={{ marginTop: 2 }}
          fullWidth
          disabled
          label="Contact Email"
          value={(identityState as User).email}
          variant="outlined"
          size="small"
        />
        <TextField
          fullWidth
          disabled
          label="Contact Phone"
          value=""
          variant="outlined"
          size="small"
        />
        <TableContainer sx={{ overflow: "visible" }}>
          <Table
            size="small"
            sx={{ "& thead th:nth-child(4)": { width: "18%" } }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  padding="none"
                  align="left"
                  sx={{ fontWeight: "bold" }}
                >
                  Description
                </TableCell>
                <TableCell
                  padding="none"
                  align="right"
                  sx={{ fontWeight: "bold" }}
                >
                  Qty
                </TableCell>
                <TableCell
                  padding="none"
                  align="right"
                  sx={{ fontWeight: "bold" }}
                >
                  Unit Price
                </TableCell>
                <TableCell
                  padding="none"
                  align="right"
                  sx={{ fontWeight: "bold" }}
                >
                  Amount
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell sx={{ padding: 0 }} align="left">
                  {`${toTitleCase(
                    props.plan.product_display_name
                  )} Plan Credits`}
                </TableCell>
                <TableCell sx={{ padding: 0 }} align="right">
                  {props.quoteSummary.totalCredits.toLocaleString()}
                </TableCell>
                <TableCell sx={{ padding: 0 }} align="right">
                  {props.plan.overage_cost_dollars}
                </TableCell>
                <TableCell sx={{ padding: 0 }} align="right">
                  {`$${formatDollars(props.quoteSummary.dollarCost)}`}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ padding: 0 }} align="left">
                  {""}
                </TableCell>
                <TableCell sx={{ padding: 0 }} align="left">
                  {""}
                </TableCell>
                <TableCell sx={{ padding: 0 }} align="right">
                  {"Sub Total"}
                </TableCell>
                <TableCell sx={{ padding: 0 }} align="right">
                  {`$${formatDollars(props.quoteSummary.dollarCost)}`}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ padding: 0 }} align="left">
                  {""}
                </TableCell>
                <TableCell sx={{ padding: 0 }} align="left">
                  {""}
                </TableCell>
                <TableCell sx={{ padding: 0 }} align="right">
                  {"Total excluding GST"}
                </TableCell>
                <TableCell sx={{ padding: 0 }} align="right">
                  {`$${formatDollars(
                    calculateTaxExcluded(props.quoteSummary.dollarCost)
                  )}`}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ padding: 0 }} align="left">
                  {""}
                </TableCell>
                <TableCell sx={{ padding: 0 }} align="left">
                  {""}
                </TableCell>
                <TableCell sx={{ padding: 0 }} align="right">
                  {`GST 10%`}
                </TableCell>
                <TableCell sx={{ padding: 0 }} align="right">
                  {`$${formatDollars(props.quoteSummary.dollarCost / 11)}`}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ padding: 0 }} align="left">
                  {""}
                </TableCell>
                <TableCell sx={{ padding: 0 }} align="left">
                  {""}
                </TableCell>
                <TableCell
                  sx={{ padding: 0, fontWeight: "bold" }}
                  align="right"
                >
                  {"Total Due"}
                </TableCell>
                <TableCell sx={{ padding: 0 }} align="right">
                  {`$${formatDollars(props.quoteSummary.dollarCost)}`}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {submitExtractJob.isError && (
          <Alert variant="outlined" severity="error">
            {(submitExtractJob.error as ReactQueryMutationError).message}{" "}
          </Alert>
        )}
        <PaymentMethodInfo style={{}} setPaymentMethod={setPaymentMethod} />
        <Typography variant="caption">
          By confirming your payment, you allow Geoscape Australia to charge you
          for this payment and save your payment information in accordance with
          their terms.
        </Typography>
      </Box>
      <Box alignSelf="flex-end" marginTop="auto" paddingTop={1} display="flex">
        <Button onClick={() => props.setSubStep(0)} variant="text">
          Back
        </Button>
        <LoadingButton
          size="small"
          loading={submitExtractJob.isPending}
          variant="contained"
          disabled={!paymentMethod}
          onClick={() => {
            submitExtractJob.mutate(props.clipConfig.id);
          }}
        >
          {`Pay $${formatDollars(props.quoteSummary.dollarCost)}`}
        </LoadingButton>
      </Box>
    </>
  );
};
